import { Component, Inject } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import {
  UploadSalesDocGQL,
  GetSalesDocTypeGQL,
  GetSalesDocByIdGQL,
  UpdateSalesDocGQL,
} from "../../../../../src/generated/graphql";
import { ImageCompressService } from "../../service/image-compress.service";
import { ProjectService } from "../../service/project.service";
import { SnackBarService } from "../../service/snack.bar.service";

export interface DialogData {
  salesDocId?: string;
}

@Component({
  selector: "upload-sales-doc-dialog",
  templateUrl: "upload-sales-doc-dialog.html",
})
export class UploadSalesDocDialog {
  projectId: string;
  salesDocType: any;
  type: string;
  salesDocId: string;
  salesDoc: any;
  result: DialogData = { salesDocId: "" };
  uploadSalesDocForm: FormGroup;
  acceptFileType = [
    "image/gif",
    "image/png",
    "image/jpg",
    "image/jpeg",
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];
  maxFileSize = 25; //25 mb
  fileBase64: string = "";
  file: File;
  constructor(
    public dialogRef: MatDialogRef<UploadSalesDocDialog>,
    private uploadSalesDocGQL: UploadSalesDocGQL,
    private getSalesDocTypeGQL: GetSalesDocTypeGQL,
    private snackBarService: SnackBarService,
    private projectSvc: ProjectService,
    private imageCompressSvc: ImageCompressService,
    private getSalesDocType: GetSalesDocTypeGQL,
    private getSalesDocById: GetSalesDocByIdGQL,
    private updateSalesDocGQL: UpdateSalesDocGQL,

    @Inject(MAT_DIALOG_DATA) private data: DialogData
  ) {
    this.result = Object.assign(this.result, this.data);
    this.result.salesDocId ? (this.type = "Update") : (this.type = "Upload");
    this.projectSvc.SwitchProjectParams();
    this.projectId = this.projectSvc.Current;
    this.initForm();
    this.getFormData();
  }

  getFormData() {
    this.getSalesDocTypeGQL.fetch().subscribe((result) => {
      if (result.errors) {
        this.snackBarService.showQueryError(result.errors);
      }
      this.salesDocType = result.data.getSalesDocType;
    });

    if (this.type == "Update") this.getSalesDoc();
  }

  private initForm() {
    this.uploadSalesDocForm = new FormGroup({
      docName: new FormControl("", Validators.required),
      type: new FormControl("", Validators.required),
      file: new FormControl(""),
      allowSalesTeam: new FormControl(true),
      allowAgency: new FormControl(true),
    });
    if (this.type == "Upload") {
      this.uploadSalesDocForm.controls.file.setValidators([
        Validators.required,
      ]);
      this.uploadSalesDocForm.updateValueAndValidity();
    }
  }

  private getSalesDoc() {
    this.getSalesDocById
      .fetch({ id: this.result.salesDocId })
      .subscribe((result) => {
        if (result?.errors) {
          this.snackBarService.showQueryError(result.errors);
        }
        this.salesDoc = result.data.getSalesDocById;
        this.patchForm();
      });
  }

  private patchForm() {
    this.uploadSalesDocForm.controls.file.setValue(
      this.salesDoc?.attachment?.fileName
    );
    this.uploadSalesDocForm.controls.docName.setValue(this.salesDoc?.docName);
    this.uploadSalesDocForm.controls.type.setValue(this.salesDoc?.type);
    this.uploadSalesDocForm.controls.allowSalesTeam.setValue(
      this.salesDoc?.allowSalesTeam
    );
    this.uploadSalesDocForm.controls.allowAgency.setValue(
      this.salesDoc?.allowAgency
    );
  }

  async fileChange(event) {
    let files: FileList = event.target.files;
    if (this.checkFile(files[0])) {
      this.uploadSalesDocForm.get("file").setValue(event.target.files[0].name);
      if (files == null && files.length === 0) return;

      // Compress image
      if (
        files[0].type != "application/pdf" &&
        files[0].type !=
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" &&
        files[0].type !=
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        let compressImg = await this.imageCompressSvc.compressImage(files[0]);
        this.file = compressImg;
      } else {
        this.file = files[0];
      }

      var reader = new FileReader();
      reader.readAsDataURL(this.file);
      reader.onload = () => {
        this.fileBase64 = reader.result.toString();
      };
      reader.onerror = (error) => {};
    } else
      this.snackBarService.show(
        "Uploaded attachment must be image/PDF and less than 25MB."
      );
  }

  private checkFile(file): boolean {
    return this.checkFileType(file) && this.checkFileSize(file);
  }
  private checkFileType(file): boolean {
    return this.acceptFileType.some((x) => x == file.type);
  }
  private checkFileSize(file): boolean {
    let fileSize = file.size / 1024 / 1024;
    return fileSize <= this.maxFileSize;
  }

  submit() {
    if (!this.result.salesDocId) {
      this.uploadSalesDocForm.markAllAsTouched();
      if (this.uploadSalesDocForm.valid) {
        this.uploadSalesDocGQL
          .mutate(
            {
              file: this.file,
              input: {
                allowAgency: this.uploadSalesDocForm.controls.allowAgency.value,
                allowSalesTeam: this.uploadSalesDocForm.controls.allowSalesTeam
                  .value,
                projectId: this.projectId,
                docName: this.uploadSalesDocForm.controls.docName.value,
                type: this.uploadSalesDocForm.controls.type.value,
              },
            },
            {
              context: {
                useMultipart: true,
              },
            }
          )
          .subscribe(
            (result) => {
              if (result) {
                this.snackBarService.show(
                  "Sales Document Uploaded Successfully"
                );
              }
              this.dialogRef.close(result);
            },
            (err) => {
              this.snackBarService.showMutationError(err);
            }
          );
      } else {
        this.snackBarService.show("Please complete all required fields");
      }
    } else if (this.result.salesDocId && this.uploadSalesDocForm.valid) {
      this.updateSalesDocGQL
        .mutate(
          {
            file: this.file,
            input: {
              id: this.data.salesDocId,
              allowAgency: this.uploadSalesDocForm.controls.allowAgency.value,
              allowSalesTeam: this.uploadSalesDocForm.controls.allowSalesTeam
                .value,
              docName: this.uploadSalesDocForm.controls.docName.value,
              projectId: this.projectId,
              type: this.uploadSalesDocForm.controls.type.value,
            },
          },
          {
            context: {
              useMultipart: true,
            },
          }
        )
        .subscribe(
          (result) => {
            if (result) {
              this.snackBarService.show("Sales Document Uploaded Successfully");
            }
            this.dialogRef.close(result);
          },
          (err) => {
            this.snackBarService.showMutationError(err);
          }
        );
    } else {
      this.snackBarService.show("Please complete all required fields");
    }
  }
}
